import { useState, useEffect, useCallback } from 'react'
import { tourApi } from '../api/tourApi'

/**
 * Custom hook para cálculos de precios de tours
 * Implementa cache y optimizaciones de performance
 */
export const useTourPricing = (tourId, tourData = {}) => {
  const [pricing, setPricing] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [cache, setCache] = useState(new Map())

  // Generar clave de cache basada en datos del tour
  const generateCacheKey = useCallback((data) => {
    const keyData = {
      tourId,
      vehicle_model_1: data.vehicle_model_1,
      r_gps: data.r_gps,
      r_insurance: data.r_insurance,
      r_cancel_insurance: data.r_cancel_insurance,
      r_single: data.r_single,
      low_seat: data.low_seat,
      discount: data.discount,
      rewards: data.rewards
    }
    return JSON.stringify(keyData)
  }, [tourId])

  // Función para calcular precios
  const calculatePricing = useCallback(async (data = {}) => {
    if (!tourId) {
      setError('ID de tour requerido')
      return
    }

    const cacheKey = generateCacheKey(data)
    
    // Verificar cache
    if (cache.has(cacheKey)) {
      const cached = cache.get(cacheKey)
      if (Date.now() - cached.timestamp < 5 * 60 * 1000) { // 5 minutos
        setPricing(cached.data)
        setLoading(false)
        setError(null)
        return
      }
    }

    setLoading(true)
    setError(null)

    try {
      const result = await tourApi.calculatePricing(tourId, data)
      
      if (result.success) {
        // Guardar en cache
        setCache(prev => new Map(prev.set(cacheKey, {
          data: result.pricing,
          timestamp: Date.now()
        })))
        
        setPricing(result.pricing)
      } else {
        setError(result.error || 'Error al calcular precios')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
    } finally {
      setLoading(false)
    }
  }, [tourId, generateCacheKey, cache])

  // Calcular precios cuando cambian los datos
  useEffect(() => {
    if (tourId) {
      calculatePricing(tourData)
    }
  }, [tourId, tourData, calculatePricing])

  // Función para recalcular precios manualmente
  const recalculate = useCallback((newData) => {
    calculatePricing(newData)
  }, [calculatePricing])

  // Función para limpiar cache
  const clearCache = useCallback(() => {
    setCache(new Map())
  }, [])

  // Función para obtener estadísticas del cache
  const getCacheStats = useCallback(() => {
    const entries = Array.from(cache.entries())
    const now = Date.now()
    
    const validEntries = entries.filter(([key, value]) => 
      now - value.timestamp < 5 * 60 * 1000
    )

    return {
      totalEntries: entries.length,
      validEntries: validEntries.length,
      expiredEntries: entries.length - validEntries.length,
      cacheSize: cache.size
    }
  }, [cache])

  return {
    pricing,
    loading,
    error,
    recalculate,
    clearCache,
    getCacheStats
  }
} 


// WEBPACK FOOTER //
// ./src/features/tours/hooks/useTourPricing.js